import _ from "lodash"
import uuid from "uuid"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import Client from "data/_client"

import propertyService from "data/property"
import { propertyChannel } from "data/websocket/sharedChannel"
import PropertyChannel from "data/websocket/propertyChannel"
import { updateValidation } from "components/table/table-automation"
import socketService, * as eventType from "data/socket"
import { local } from "data/lib/local-json"
import { parallel } from "data/lib/promise-helpers"

// private module logic //

interface IValidationErrors {
  errordetail?: object,
  calcDetail?: {errors: Array<{}>},
  show?: boolean,
  filters?: Array<Function>
  onClose?: Function
}
type ValidationError = {
  parentresource: string
  parentresourceid: number
  resource: string
  resourceid: number
  validationdetail: object
}

const validationStream = new BehaviorSubject<IValidationErrors>({errordetail: {}, calcDetail: {errors: []}})
const next = (state) => {
  validationStream.next(state)
}
function addFilter(fn) {
  const current = validationStream.getValue()
  const filters = current.filters || []
  if (_.includes(filters, fn)) return
  next({...current, filters: [...filters, fn]})
}
function removeFilter(fn) {
  const current = validationStream.getValue()
  const filters = current.filters || []
  if (!_.includes(filters, fn)) return
  next({...current, filters: _.reject(filters, f => f === fn)})
}

const sleep = (ms) => new Promise(r => setTimeout(r,ms))

async function fetch(resource, id) {
  const errors = await Client.get(`validation?resource=eq.${resource}&resourceid=eq.${id}`)
  update(errors, resource, id)
}

async function fetchAll(resource) {
  await sleep(2000)
  const errors = await Client.get(`validation?resource=eq.${resource}`)
  update(errors, resource)
}

async function fetchByParent(resource: string, parentresource: string, parentresourceid: number) {
  await sleep(2000)
  const errors = await Client.get(`validation?resource=eq.${resource}&parentresource=eq.${parentresource}&parentresourceid=eq.${parentresourceid}`)
  update(errors, resource)
}

async function fetchAllByParent(parentresource: string, parentresourceid: number) {
  const errors = await Client.get(`validation?parentresource=eq.${parentresource}&parentresourceid=eq.${parentresourceid}`)
  update(errors, parentresource, parentresourceid)
}

function update(errors:ValidationError[], resource?, id?) {
  let current = _.cloneDeep(validationStream.getValue())
  if (id) {
    _.unset(current, ['errordetail', resource, id])
  } else if (resource) {
    _.unset(current, ['errordetail', resource])
  }
  if (id && resource) { // if these were parents, update child validation too
    for (let resource_ in current.errordetail) {
      for (let id_ in current.errordetail[resource_])
        for (let error of current.errordetail[resource_][id_]) {
          if (error._object[resource+'id'] == id) {
            _.unset(current, ['errordetail', resource_, id_])
          }
        }
    }
  }
  for (let {resource, resourceid} of errors) {
    _.unset(current, ['errordetail',resource,resourceid])
  }
  const nested = _.reduce(
    errors,
    (nested, error) => _.setWith(nested, [error.resource, error.resourceid], error.validationdetail, Object),
    {})
  _.merge(current['errordetail'], nested)
  next(current)
  updateValidation()
}

async function validate(resource: string, id?: number, taskid?: string): Promise<object> {
  const channelid: number = id || +localStorage.property
  const response = await Client.post(`validate/${resource}/${id || ''}`, {channelid, taskid}) // params must be all lowercase for backend
  await id ? fetch(resource, id) : fetchAll(resource)
  return response
}

async function validateByParent(resource: string, parentresource: string, parentid: number) {
  const response = await Client.post(`validate/${resource}/by-parent/${parentresource}/${parentid}`, {})
  await fetchByParent(resource, parentresource, parentid)
  return response
}

type Property = {code: string, propertyid: number}
function validateAfterChange(beforeChange: object, afterChange: object, currentProperties: object[]): Promise<any> {
  const folderId: number = _.last(local('folderPath'))

  // if code changed, revalidate and reload all properties that have the same code (to detect code collisions),
  // otherwise only revalidate and reload the property that changed
  const comparator = (o1, o2) => o1.code.toLowerCase() == o2.code.toLowerCase() || o1.propertyid == o2.propertyid
  const properties = (currentProperties as Property[]).filter(p => _.isEqualWith(p, beforeChange, comparator) || _.isEqualWith(p, afterChange, comparator))

  return parallel(properties.map(p => () => {
    const taskId: string = uuid()
    if (p.propertyid === propertyService.get()?.propertyid) return Promise.resolve()//validate(p.propertyid, taskId)
    return PropertyChannel
      .do(p.propertyid, () => validate('property', p.propertyid, taskId))
      .thenResolveOnMessage(message => message.taskid === taskId)
  }), 10)
  .then(() => {
    let promise // because ts gets confused by implicit Promise<T> unions https://github.com/microsoft/TypeScript/issues/20531
    if(!properties.length) promise = Promise.resolve()
    else if(properties.length === 1) promise = propertyService.fetchProperty(_.get(properties, "[0].propertyid"))
    else promise = propertyService.fetchByFolderId(folderId, false)
    return promise
  })
  .then(() => updateValidation())
}

function displayValidationErrors(errors?: IValidationErrors, filter?: Function): void {
  if (filter) {
    addFilter(filter)
    errors = validationService.filteredValue(errors)
    next({...errors, show: true, onClose: () => removeFilter(filter)})
    return
  }
  errors = errors || validationStream.getValue()
  next({...errors, show: true})
}

function hideValidationErrors(){
  const { onClose } = validationStream.getValue()
  if(onClose) onClose()
  next({...validationStream.getValue(), show: false, onClose: undefined})
}

let refetch
const socketSubscription = socketService.stream().subscribe(data => {
  switch(true) {
    case data.eventType === eventType.PROPERTY_SELECTED:
      const current = validationStream.getValue()
      const errordetail = _.pick(current.errordetail, ['folder'])
      validationStream.next({errordetail, calcDetail: {errors: []}})
      validate('property', data.event.propertyId, '')
      refetch = _.debounce(() => propertyService.fetchProperty(data.event.propertyId), 300)
      break
    case data.eventType === eventType.PROPERTY_UPDATED:
      if (!refetch) refetch = _.debounce(() => propertyService.fetchProperty(data.event.channelId), 300)
      refetch()
  }
})

function filteredValue(errordetail?) {
  let err = _.cloneDeep(validationStream.getValue())
  _.merge(err.errordetail, errordetail)
  for (let filter of err.filters || []){
    err = filter(err)
  }
  return err
}


// public module interface //

const validationService = {
  addFilter,
  displayValidationErrors,
  fetch,
  fetchAll,
  fetchAllByParent,
  fetchByParent,
  hideValidationErrors,
  removeFilter,
  stream: () => validationStream.asObservable(),
  validate,
  validateAfterChange,
  validateByParent,
  value: () => validationStream.getValue(),
  filteredValue
}

export { IValidationErrors }
export default validationService
